import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * `MoreOptionsMenu` — shared "More options" dropdown for collapsing
 * secondary header actions on mobile (or anywhere else a labelled
 * dropdown is wanted). One look, one behaviour, applied across pages
 * to prevent visual drift.
 *
 * Props
 *  - groups: [{
 *      label?: string,                       // optional section header
 *      items: [{
 *        label:    string,
 *        icon?:    React.ReactNode,          // e.g. <BarChart3 className="..." />
 *        onClick:  () => void,
 *        disabled?: boolean,
 *        testid?:  string,
 *        className?: string,                 // optional MenuItem-level class (e.g. emerald accent)
 *      }],
 *    }]
 *  - triggerLabel: defaults to "More options".
 *  - triggerClassName: extra classes on the trigger button.
 *  - testid: data-testid for the trigger button.
 *  - align: 'start' | 'center' | 'end' (default 'end').
 *  - className: wrapper className (e.g. `md:hidden` for mobile-only).
 */
export const MoreOptionsMenu = ({
  groups = [],
  triggerLabel = 'More options',
  triggerClassName = 'border-slate-300 flex-shrink-0',
  testid = 'more-options-button',
  align = 'end',
  className = '',
}) => {
  if (!groups || groups.length === 0) return null;
  // Filter out empty groups so we never render an empty section.
  const visibleGroups = groups.filter((g) => g && g.items && g.items.length > 0);
  if (visibleGroups.length === 0) return null;

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={triggerClassName}
            data-testid={testid}
            aria-label={triggerLabel}
          >
            <MoreHorizontal className="mr-2 h-4 w-4" />
            {triggerLabel}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-56">
          {visibleGroups.map((group, gi) => (
            <React.Fragment key={`${group.label || 'group'}-${gi}`}>
              {gi > 0 && <DropdownMenuSeparator />}
              {group.label && <DropdownMenuLabel>{group.label}</DropdownMenuLabel>}
              {group.items.map((item, ii) => (
                <DropdownMenuItem
                  key={item.testid || `${gi}-${ii}-${item.label}`}
                  onClick={item.onClick}
                  disabled={!!item.disabled}
                  className={item.className}
                  data-testid={item.testid}
                >
                  {item.icon}
                  {item.icon ? <span className="ml-2">{item.label}</span> : item.label}
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default MoreOptionsMenu;
